import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Search, Loader2, Shield, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 24;

export default function Generals() {
  const { lang, t, dir } = useLang();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [article, setArticle] = useState("");
  const [data, setData] = useState({ items: [], total: 0, facets: [] });
  const [facets, setFacets] = useState([]);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => { setSkip(0); }, [debounced, article, lang]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const p = new URLSearchParams({ lang, limit: PAGE, skip });
      if (debounced) p.set("search", debounced);
      if (article) p.set("article", article);
      const { data: d } = await axios.get(`${API}/generals?${p}`);
      setData((prev) => ({ ...d, items: skip === 0 ? d.items : [...prev.items, ...d.items] }));
      if (skip === 0 && d.facets?.length) setFacets(d.facets);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [lang, skip, debounced, article]);

  useEffect(() => { load(); }, [load]);

  const countLabel = `${data.total} ${data.total === 1 ? t("common.result") : t("common.results")}`;

  return (
    <div className="es-page" data-testid="page-generals" dir={dir}>
      <Seo
        title="Evony Generals — Rankings, Buffs & Guides | Evony Strategist"
        description="Browse and search Evony generals. See which strategy guides each general appears in, sourced from the Evony Guide Wiki."
        path="/generals"
      />
      <header className="es-page-head">
        <h1 className="es-h1"><Shield size={22} className="es-h1-icon" /> {t("generals.title")}</h1>
        <p className="es-lead">{t("generals.subtitle")}</p>
      </header>

      <div className="es-search">
        <Search size={18} className="es-search-icon" />
        <input data-testid="generals-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("generals.search")} aria-label={t("generals.search")} />
      </div>

      {facets.length > 0 && (
        <div className="es-facets" data-testid="generals-facets">
          <button className={`es-facet ${!article ? "active" : ""}`} onClick={() => setArticle("")} data-testid="generals-facet-all">{t("common.all")}</button>
          {facets.map((f) => (
            <button key={f.slug} className={`es-facet ${article === f.slug ? "active" : ""}`}
              data-testid={`generals-facet-${f.slug}`} onClick={() => setArticle(article === f.slug ? "" : f.slug)}>
              {f.title} <span className="es-facet-count">{f.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="es-result-count" data-testid="generals-count">{countLabel}</div>

      {error ? (
        <div className="es-state" data-testid="generals-error">{t("common.networkError")}
          <button className="es-btn-ghost" onClick={load}>{t("common.retry")}</button></div>
      ) : data.items.length === 0 && !loading ? (
        <div className="es-state" data-testid="generals-empty">{t("common.noResults")}</div>
      ) : (
        <div className="es-card-grid" data-testid="generals-grid">
          {data.items.map((g) => (
            <Link key={g.slug} to={`/generals/${g.slug}`} className="es-entity-card" data-testid={`general-card-${g.slug}`}>
              <div className="es-entity-head">
                <span className="es-entity-name">{g.localizedName || g.name}</span>
                <ChevronRight size={16} className="es-entity-chev" />
              </div>
              {g.localizedName && g.localizedName !== g.name && <span className="es-entity-sub">{g.name}</span>}
              {g.articles?.length > 0 && (
                <div className="es-entity-tags">
                  <span className="es-tag-label">{t("common.appearsIn")}</span>
                  {g.articles.slice(0, 3).map((a) => <span key={a.slug} className="es-tag">{a.title}</span>)}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {loading && <div className="es-state"><Loader2 className="es-spin" size={20} /> {t("common.loading")}</div>}
      {!loading && data.items.length < data.total && (
        <div className="es-loadmore-wrap">
          <button className="es-btn-secondary" data-testid="generals-loadmore" onClick={() => setSkip(skip + PAGE)}>
            {t("common.showMore")}
          </button>
        </div>
      )}
    </div>
  );
}
